import { APVendorSummary, JobSummary } from './AccountsPayables.interfaces';
import { Approval } from '../admin/ApprovalSequence.interface';

export type ACForm = {
  label: string;
  url?: string;
  access?: string;
};

export type NewContractFields = ContractInfo &
  JobInfo &
  GeneralInfo &
  ContractAttachments;

export type ContractInfo = {
  contract?: string;
  contractName: string;
  department: number;
  customer: number;
  retainage: number;
  processGroup?: string;
  startDate: Date;
};

export type JobInfo = {
  projectManager: number;
  prState: string;
  taxCode: string;
  reviewerGroup?: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: number;
  county: string;
  physician: string;
  notice: string;
  extraJob: string;
};

export type GeneralInfo = {
  bondCost: number;
  bondPercentage: number;
  bondReqd: string;
  dateOrdered: Date;
  insurance: string;
  insuranceDateOrdered: Date;
  contractValue: number;
  supervision: number;
  targetCost: number;
  estProfit?: number;
  estProfitPercentage?: number;
  estCostMarkUp?: number;
  jobType: string;
  certified: string;
  ccip: string;
  dwellingUnits: number;
  metric1: string;
  metric1Amt: number;
  metric2: string;
  metric2Amt: number;
  heated: number;
  parking: string;
  fieldPS?: string;
};

export type ContractAttachments = {
  attachments?: File[];
};

export type ContractDraftAttachment = {
  name: string;
  uniqueName: string;
  size: number;
};

export type ContractDraft = {
  id: string;
  payload: Partial<Omit<NewContractFields, 'attachments'>>;
  draftAttachments: ContractDraftAttachment[];
  attachmentsSize: number;
  createdAt: string;
  updatedAt: string;
};

export type DepartmentSummary = {
  Department: string;
  Description: string;
};

export type CustomerSummary = {
  accountNumber: number;
  name: string;
  sortName: string;
};

export type ProcessGroupSummary = {
  ProcessGroup: string;
  Description: string;
};

export type ProjectManagerSummary = {
  ProjectMgr: number;
  Name: string;
};

export type StateSummary = {
  State: string;
  Name: string;
};

export type TaxCodeSummary = {
  TaxCode: string;
  Description: string;
};

export type ReviewerSummary = {
  ReviewerGroup: string;
  Description: string;
};

export type GeneralInfoValue = {
  DBValue: string;
  Display: string;
};

export type EmployeeSummary = {
  Name: string;
  SortName: string;
};

export type SuccessFiles = {
  fileName: string;
  amount: number;
};

export type JobCostCode = {
  CostCode: string;
  Description: string;
};

export type UnitOfMeasure = {
  UM: string;
  Description: string;
};

export type JobExtra = {
  ExDesc: string;
  ExNumber: string;
};

export type POExtra = {
  id?: string;
  extra?: string;
  costCode?: string;
  um?: string;
  quantity?: number;
  price?: number;
  total?: number | null;
};

export type POPieceWork = {
  description: string;
  extras?: POExtra[];
};

export type POPieceWorkForm = {
  pieceWork: POPieceWork[];
};

export type POUploadTypes = 'file' | 'manual';

export type POGeneralInfo = {
  job: string;
  poType: string;
  vendor: number;
  poDate: Date;
  month: Date;
  multipleYN: string;
  identifier: string;
  description: string;
  poID: string;
  upType: POUploadTypes;
};

export type POFileForm = {
  poFile: File;
};

export type POForm = POGeneralInfo & POPieceWorkForm & POFileForm;

export type POItem = {
  costCode: string;
  extraNumber: string;
  pieceWorkDesc: string;
  unitOfMeasure: string;
};

export type POItemArgs =
  | (POItem & { units: number; cost: number })
  | (POItem & { total: number });

export type POItemSummary = POItem & {
  units?: number;
  cost?: number;
  total?: number;
};

type POGeneralArgs = {
  companyID: string;
  job: string;
  vendor: number;
  poDate: Date;
  poDesc: string;
  po: string;
};

export type POArgs = POGeneralArgs & {
  poItems?: POItemArgs[];
  poFile?: File;
};

export type COEstimate = {
  costType: number;
  ctLabel: string;
  um: string;
  umLabel: string;
  cost: number;
};

export type COPhase = {
  code: string | null;
  description?: string;
  details?: COEstimate[] | null;
};

export type COLine = {
  item: number;
  description: string;
  amount: number;
  phases?: COPhase[];
};

export type COLineDraft = Partial<COLine>;

export type COFormFields = {
  customer: number;
  job: string;
  changeOrder: string;
  description: string;
  totalAmount: number;
  lines?: COLineDraft[];
  file: File;
};

export type SLGeneralInfo = {
  job: string;
  vendor: number;
  description: string;
  sl: string;
  upType: POUploadTypes;
  attachment: File;
};

export type SubFileForm = {
  subFile: File;
};

export type SLItem = {
  line: number;
  description?: number;
  phase?: string;
  um?: string;
  units?: number;
  price?: number;
  total?: number | null;
  retainage?: number;
};

export type SLItems = {
  slItems?: SLItem[];
};

export type SLForm = SLGeneralInfo & SLItems & SubFileForm;

export type SLArgs = {
  job: string;
  vendor: number;
  description: string;
  sl: string;
  slItems?: SLItem[];
  subFile?: File;
  attachment?: File;
};

export type SubcontractEntry = {
  job: JobSummary;
  vendor: APVendorSummary;
  description: string;
  sl: string;
  slItems: SLItem[];
  attachment?: string;
};

export type PayrollTimesheetDetail = {
  id?: string;
  regularHrs?: number;
  overTimeHrs?: number;
  sickHrs?: number;
  vacationHrs?: number;
  holidayHrs?: number;
  perDiem?: number;
  job?: string;
  phaseCode?: string;
  totalHrs?: number;
};

export type PayrollTimesheetDetails = PayrollTimesheetDetail & {
  id: string;
  date?: Date;
  dateDetails?: PayrollTimesheetDetail[];
  useDateDetails?: boolean;
  certified?: boolean;
};

export type EmployeeTimesheet = PayrollTimesheetDetail & {
  empID: string;
  lastName: string;
  firstName: string;
  salaried: boolean;
  details?: PayrollTimesheetDetails[];
  useDetails?: boolean;
};

export type PayrollTimesheetDetailArgs = {
  regularHrs?: number;
  overTimeHrs?: number;
  sickHrs?: number;
  vacationHrs?: number;
  holidayHrs?: number;
  perDiem?: number;
  job?: string;
  phaseCode?: string;
};

export type PayrollTimesheetDateDetailArgs = PayrollTimesheetDetailArgs & {
  id: string;
};

export type TimeSheetDetailsArgs = PayrollTimesheetDetailArgs & {
  id: string;
  date?: Date;
  dateDetails?: PayrollTimesheetDateDetailArgs[];
};

export type TimeSheetArgs = (PayrollTimesheetDetailArgs & {
  empID: string;
  details?: PayrollTimesheetDetailArgs[];
})[];

export type PRTimesheetSummary = {
  id: string;
  updatedAt: Date;
  submitted: boolean;
  userName: string;
};

export type SubcontractCOFormItem = {
  item: number;
  description?: string;
  phase?: string;
  um?: string;
  units?: number;
  unitCosts?: number;
  total?: number;
  retainage?: number;
  isNew: boolean;
  isEdited: boolean;
  pending: boolean;
  invoicedCost?: number;
};

export type SubcontractCOFormFields = {
  sl: string;
  items?: SubcontractCOFormItem[];
};

export type SubcontractCOItem = {
  item: number;
  description: string;
  phase: string;
  um: string;
  units: number;
  unitCosts: number;
  total: number;
  retainage: number;
  pending: boolean;
  invoicedCost?: number;
};

export type SubcontractCONewItemBaseArgs = Omit<SubcontractCOItem, 'pending'>;

export type SubcontractCONewItemArgs =
  | Omit<SubcontractCONewItemBaseArgs, 'units' | 'unitCosts'>
  | Omit<SubcontractCONewItemBaseArgs, 'total'>;

export type SubcontractChangeOrderArgs = {
  sl: string;
  newItems?: SubcontractCONewItemArgs[];
  editItems?: Omit<
    SubcontractCONewItemArgs,
    'phase' | 'retainage' | 'description' | 'um' | 'unitCosts'
  >[];
};

export type SubcontractChangeOrderResult = {
  sl: string;
  newItems?: Omit<SubcontractCOItem, 'pending'>[];
  editItems?: Omit<SubcontractCOItem, 'pending'>[];
};

export enum APTransactionItemType {
  jc = 'jc',
  gl = 'gl',
}

export type APTransactionEntryItemBase = {
  description: string;
  total: number;
};

export type APTransactionEntryJobItem = APTransactionEntryItemBase & {
  phase: string;
  costType: number;
  job: string;
  um: string;
  units?: number;
  price?: number;
  total?: number;
};

export type APTransactionEntryGLItem = APTransactionEntryItemBase & {
  glAcct: string;
};

export type APTransactionEntryJobItemDraft = Partial<APTransactionEntryJobItem>;
export type APTransactionEntryGLItemDraft = Partial<APTransactionEntryGLItem>;

export type APTransactionEntryBaseFields = {
  vendorType: string;
  vendor: string;
  date: Date;
  description: string;
  number: string;
  total: number;
  attachment?: File;
  prevAttachment?: string;
  removeAttachment?: boolean;
};

export type APTransactionEntryForm = Partial<APTransactionEntryBaseFields> & {
  items?: (
    | ({ type: APTransactionItemType.jc } & APTransactionEntryJobItemDraft)
    | ({ type: APTransactionItemType.gl } & APTransactionEntryGLItemDraft)
  )[];
};

export type APTransactionEntryArgs = Omit<
  Partial<APTransactionEntryBaseFields>,
  'vendorType' | 'total' | 'removeAttachment' | 'prevAttachment'
> & {
  companyID: string;
  items?: (APTransactionEntryJobItemDraft | APTransactionEntryGLItemDraft)[];
};

export type APTransactionEntryUpdateArgs = Omit<
  APTransactionEntryArgs,
  'companyID'
> & {
  removeAttachment?: boolean;
  fromApproval?: boolean;
};

export type APTransactionEntryDraft = Omit<
  Partial<APTransactionEntryBaseFields>,
  'items' | 'attachment' | 'removeAttachment' | 'prevAttachment'
> & {
  id: string;
  items?: (APTransactionEntryJobItemDraft | APTransactionEntryGLItemDraft)[];
  attachmentName?: string;
  attachmentPath?: string;
  submitted?: boolean;
  approved?: boolean;
  approvalStep?: number;
  approvals?: Approval[];
  lastStep?: number;
  user?: { id: string; userName: string };
};

export type APEntryDraftSummary = {
  id: string;
  updatedAt: string;
  submitted: boolean;
  number?: string;
  userName?: string;
};

export type FormHistoryItem = {
  id: string;
  section: string;
  name: string;
  summary: string;
  createdAt: string;
  updatedAt: string;
  user: { id: string; firstName: string; lastName: string; email: string };
};

export type DocumentDropOffFields = {
  documents: File[];
  notes: string;
  attentionTo?: string;
};
